<template>
    <div v-if="isLoading" class="loading-container">
        <img :src="require('@/assets/10.png')" alt="Logo" class="loading-logo" />
    </div>
    <div v-else>
        <div class="slide">
            <div class="bg-img">
                <div class="slide-content">
                    <div><h2 class="slide-header">{{$t('message.ourOrders')}}</h2></div>
                <div>
                    <h5 class="slide-h5">{{$t('message.home')}}</h5>
                    <b-icon :icon="isRTL ? 'chevron-double-left' : 'chevron-double-right'" class="icon-slider"></b-icon>
                    <h5 class="slide-h5"> {{$t('message.orders')}}</h5>
                </div>
            </div>
            </div>
        </div>
        <div class="container custom-container-orders mt-5">
            <b-row class="">
                <b-col cols="12" md="6" class="mb-3 mb-md-0">
                    <div class="d-flex align-items-center">
                        <h3 class="filter-text">{{ $t('message.filter') }}</h3>
                        <b-form-input class="filter-input" id="filter-input" v-model="filter"
                        :placeholder="$t('message.adminName')"></b-form-input>
                        <b-button variant="primary" @click="showFilterModal = true" class="ml-2 filter-btn ms-2">
                            <b-icon icon="funnel-fill"></b-icon>
                            {{ $t('message.advancedFilters') }}
                        </b-button>
                    </div>
                </b-col>
                <b-col cols="12" md="6" class="text-right d-flex justify-content-end align-items-center">
                    <div v-if="totalCount !== null" class="total-count-badge me-3">
                        <b-icon icon="list-check" class="me-1"></b-icon>
                        <span class="count-label">{{ $t('message.totalResults') }}:</span>
                        <span class="count-number">{{ totalCount }}</span>
                    </div>
                    <b-button variant="warning" @click="newOrder()" class="new-order">{{ $t('message.addNewOrder') }}</b-button>
                </b-col>
            </b-row>

            <b-row>
                <b-col v-for="order in filteredRequests" :key="order.id" cols="12" md="6" class="mb-4 mt-4">
                    <div class="phone-card">
                        <div class="phone-card-body" :style="{ backgroundColor: getStatusColor(order.order_status, order.need_approval) }">
                            <!-- Horizontal Battery Icon Left -->
                            <div class="battery-wrapper" :class="getStatusClass(order.order_status, order.need_approval)">
                                <svg viewBox="0 0 50 26" class="battery-horizontal">
                                    <!-- Battery Body Outline -->
                                    <rect x="2" y="4" width="42" height="18" rx="3" fill="currentColor" opacity="0.25" stroke="currentColor" stroke-width="2.5"/>
                                    <!-- Battery Fill (from left) -->
                                    <rect x="2" y="4" :width="getBatteryFill(order.order_status)" height="18" rx="3" fill="currentColor"/>
                                    <!-- Battery Tip (Right) -->
                                    <rect x="44" y="9" width="4" height="8" rx="1.5" fill="currentColor"/>
                                </svg>
                            </div>

                            <!-- Order Info Right -->
                            <div class="order-info-area">
                                <div class="info-row">
                                    <span class="info-key">{{$t('message.orderNumber')}}</span>
                                    <span class="info-val">{{ order.id }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-key">{{$t('message.addedBy')}}</span>
                                    <span class="info-val">{{ order.adminName }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-key">{{$t('message.technicalPerson')}}</span>
                                    <span class="info-val">{{ order.technicalLeaderName }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-key">{{$t('message.customerNameLabel')}}</span>
                                    <span class="info-val">{{ order.customer_name }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-key">{{$t('message.status')}}</span>
                                    <span class="info-val status-val" :class="getStatusClass(order.order_status, order.need_approval)">
                                        {{statusName(order.order_status, order.need_approval)}}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Show Details Footer -->
                        <div class="details-footer" @click="showDetails(order.id)">
                            {{$t('message.showDetails')}}
                        </div>
                    </div>
                </b-col>
                

                <template v-if="filteredRequests.length === 0">
                        <div class="text-center p-4" style="width: 100%;">
                            <!-- <lord-icon src="https://cdn.lordicon.com/wkppeqfv.json"
                                    colors="primary:#121331,secondary:#edd100" trigger="loop" style="width:100px;height:100px">
                                </lord-icon> -->
                            <img :src="require('../assets/not-found.svg')" alt="404 Photo" style="width:100%;">
                            <p class="mb-0 filter-result"> {{ $t('message.noMatchingResults') }} "{{ filter }}".</p>
                        </div>
                        </template>
            </b-row>
            <div class="mt-5 pagination">
                <b-pagination v-model="currentPage" pills :total-rows="rows" :per-page="perPage"></b-pagination>
            </div>
        </div>

        <!-- Advanced Filters Modal -->
        <b-modal v-model="showFilterModal" :title="$t('message.advancedFilters')" size="lg" centered hide-footer>
            <b-form @submit.prevent="applyFilters">
                <b-row>
                    <!-- From Date -->
                    <b-col cols="12" md="6" class="mb-3">
                        <label class="filter-label">{{ $t('message.fromDate') }}</label>
                        <b-form-datepicker 
                            v-model="filters.from_date" 
                            :placeholder="$t('message.selectDate')"
                            class="custom-datepicker"
                            :locale="$i18n.locale"
                            date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                            reset-button
                        ></b-form-datepicker>
                    </b-col>

                    <!-- To Date -->
                    <b-col cols="12" md="6" class="mb-3">
                        <label class="filter-label">{{ $t('message.toDate') }}</label>
                        <b-form-datepicker 
                            v-model="filters.to_date" 
                            :placeholder="$t('message.selectDate')"
                            class="custom-datepicker"
                            :locale="$i18n.locale"
                            date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
                            reset-button
                        ></b-form-datepicker>
                    </b-col>

                    <!-- Order Status -->
                    <b-col cols="12" md="6" class="mb-3">
                        <label class="filter-label">{{ $t('message.orderStatus') }}</label>
                        <b-form-select 
                            v-model="filters.order_status" 
                            :options="statusOptions"
                            class="custom-select"
                        >
                            <template #first>
                                <b-form-select-option :value="null">{{ $t('message.allStatuses') }}</b-form-select-option>
                            </template>
                        </b-form-select>
                    </b-col>

                    <!-- Equipment Name -->
                    <b-col cols="12" md="6" class="mb-3">
                        <label class="filter-label">{{ $t('message.equipmentName') }}</label>
                        <b-form-input 
                            v-model="filters.equipment_name" 
                            :placeholder="$t('message.enterEquipmentName')"
                            class="custom-input"
                        ></b-form-input>
                    </b-col>

                    <!-- Company Name -->
                    <b-col cols="12" md="6" class="mb-3">
                        <label class="filter-label">{{ $t('message.coustomerCompanyName') }}</label>
                        <b-form-input 
                            v-model="filters.company_name" 
                            :placeholder="$t('message.coustomerCompanyName')"
                            class="custom-input"
                        ></b-form-input>
                    </b-col>

                    <!-- Technician Name -->
                    <b-col cols="12" class="mb-3">
                        <label class="filter-label">{{ $t('message.technicianName') }}</label>
                        <b-form-input 
                            v-model="filters.technician_name" 
                            :placeholder="$t('message.enterTechnicianName')"
                            class="custom-input"
                        ></b-form-input>
                    </b-col>
                </b-row>

                <!-- Modal Actions -->
                <b-row class="mt-4">
                    <b-col cols="6">
                        <b-button variant="outline-secondary" block @click="clearFilters" class="action-btn">
                            <b-icon icon="x-circle"></b-icon>
                            {{ $t('message.clearFilters') }}
                        </b-button>
                    </b-col>
                    <b-col cols="6">
                        <b-button variant="primary" block type="submit" class="action-btn">
                            <b-icon icon="check-circle"></b-icon>
                            {{ $t('message.applyFilters') }}
                        </b-button>
                    </b-col>
                </b-row>
            </b-form>
        </b-modal>
    </div>
</template>

<script>
import axiosInstance from '../axios-config';
import { mapGetters } from 'vuex'

export default {
    name: "about",
    components: {
        //   slider,
    },
    data() {
        return {
            filter: '',
            orders: [],
            rows: 0,
            currentPage: 1,
            perPage: 10,
            isLoading: true,
            showFilterModal: false,
            totalCount: null,
            filters: {
                from_date: null,
                to_date: null,
                order_status: null,
                equipment_name: '',
                company_name: '',
                technician_name: ''
            },
            statusOptions: [
                { value: '0', text: 'Pending' },
                { value: '1', text: 'Started' },
                { value: '2', text: 'In Progress' },
                { value: '3', text: 'Completed' },
                { value: '4', text: 'Decline' },
                { value: '5', text: 'Need Approval' },
                { value: '6', text: 'In Process' }
            ]
        };

    },
    computed: {
        ...mapGetters(['getUser']),
        company_id() {
            return this.getUser; // استخدام الـ getter لجلب بيانات المستخدم
        },
        filteredRequests() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            const filtered = this.orders.filter(order =>
                order.adminName.toLowerCase().includes(this.filter.toLowerCase())
            );
            this.rows = filtered.length; // تحديث إجمالي الصفوف بعد التصفية
            return filtered.slice(start, end);
        },
        isRTL() {
            return this.$i18n.locale === 'ar';
        },
    },
    created() {
        this.fetchData();
    },
    methods: {

        statusName(orderStatus, needApproval) {
            // Handle status 5 with needApproval
            if (orderStatus == 5) {
                if (needApproval == 1) {
                    return 'Need Approval';
                } else if (needApproval == 3) {
                    return 'Approved';
                } else if (needApproval == 4) {
                    return 'Approval Refused';
                }
            }
            
            // Handle other statuses
            const statusMap = {
                0: 'Pending',
                1: 'Started',
                2: 'Started',
                3: 'Completed',
                4: 'Decline',
                6: 'In Process',
            };
            return statusMap[orderStatus] || 'Unknown Status';
        },
        getStatusColor(orderStatus, needApproval) {
            // Handle status 5 with needApproval
            if (orderStatus == 5) {
                if (needApproval == 1) {
                    return 'rgba(255, 87, 34, 0.28)'; // Orange - more saturated
                } else if (needApproval == 3 || needApproval == 4) {
                    return 'rgba(254, 194, 0, 0.32)'; // Yellow - more saturated
                }
            }
            
            // Handle other statuses - more saturated colors like Android
            const colorMap = {
                0: 'rgba(119, 125, 133, 0.25)',     // Grey - more visible
                1: 'rgba(255, 0, 0, 0.25)',         // Red - more saturated
                2: 'rgba(255, 0, 0, 0.25)',         // Red - more saturated
                3: 'rgba(91, 194, 54, 0.35)',       // Green - more saturated
                4: 'rgba(255, 0, 0, 0.25)',         // Red - more saturated
                6: 'rgba(254, 194, 0, 0.32)',       // Yellow - more saturated
            };
            return colorMap[orderStatus] || '#FFFFFF'; // Default to white
        },

        getStatusClass(orderStatus, needApproval) {
            // Handle status 5 with needApproval
            if (orderStatus == 5) {
                if (needApproval == 1) {
                    return 'status-orange';
                } else if (needApproval == 3 || needApproval == 4) {
                    return 'status-yellow';
                }
            }
            
            // Handle other statuses
            const classMap = {
                0: 'status-grey',
                1: 'status-red',
                2: 'status-red',
                3: 'status-green',
                4: 'status-red',
                6: 'status-yellow',
            };
            return classMap[orderStatus] || 'status-grey';
        },

        getBatteryFill(orderStatus) {
            // Battery fill width based on status (out of 42)
            const fillMap = {
                0: '8',      // Pending - almost empty
                1: '12',     // Started - low
                2: '18',     // In Progress - medium
                3: '42',     // Completed - full
                4: '8',      // Decline - almost empty
                5: '28',     // Need approval - medium-high
                6: '36',     // In Process - high
            };
            return fillMap[orderStatus] || '18';
        },

        async fetchData() {
            try {
                const formData = new FormData();
                // formData.append('current_date', '2023-12-14');
                formData.append('offset', 0);
                formData.append('admin_id', this.company_id.id);
                formData.append('is_pm_order', 0);
                
                // Add filter parameters if they exist
                if (this.filters.from_date) {
                    console.log('From Date (Original):', this.filters.from_date, 'Type:', typeof this.filters.from_date);
                    const formattedFromDate = this.formatDateToYMD(this.filters.from_date);
                    console.log('From Date (Formatted YYYY-MM-DD):', formattedFromDate);
                    formData.append('from_date', formattedFromDate);
                }
                if (this.filters.to_date) {
                    console.log('To Date (Original):', this.filters.to_date, 'Type:', typeof this.filters.to_date);
                    const formattedToDate = this.formatDateToYMD(this.filters.to_date);
                    console.log('To Date (Formatted YYYY-MM-DD):', formattedToDate);
                    formData.append('to_date', formattedToDate);
                }
                if (this.filters.order_status !== null) {
                    formData.append('order_status', this.filters.order_status);
                }
                if (this.filters.equipment_name) {
                    formData.append('equipment_name', this.filters.equipment_name);
                }
                if (this.filters.company_name) {
                    formData.append('company_name', this.filters.company_name);
                }
                if (this.filters.technician_name) {
                    formData.append('technician_name', this.filters.technician_name);
                }
                
                const response = await axiosInstance.post('admin/select_orders_by_admin_id.php', formData);

                console.log(response.data);
                if (response.data.status === "true") {
                    this.orders = response.data.output;
                    // Update total count from response
                    if (response.data.total_count !== undefined) {
                        this.totalCount = response.data.total_count;
                        console.log('Total Count:', this.totalCount);
                    }
                    console.log('orders = ', this.orders);
                    this.isLoading = false;

                }
            } catch (error) {
                console.error('Error logging in:', error)
                this.$izitoast.error({
                    title: 'Error',
                    message: 'An error occurred while trying to get data',
                    position: 'bottomLeft',
                    timeout: 5000,
                });
                this.isLoading = false;
            }
        },
        showDetails(itemId){
            console.log(111111111);
            // this.$router.push('show-details/' + itemId);
            this.$router.push(`/orders/${itemId}/details`);
            // /orders/:id/details
        },
        newOrder(){
            this.$nextTick(() => {
                this.$router.push('/addNewOrder');
            });
        },
        applyFilters() {
            this.isLoading = true;
            this.showFilterModal = false;
            this.fetchData();
        },
        clearFilters() {
            this.filters = {
                from_date: null,
                to_date: null,
                order_status: null,
                equipment_name: '',
                company_name: '',
                technician_name: ''
            };
            this.isLoading = true;
            this.showFilterModal = false;
            this.fetchData();
        },
        formatDateToYMD(dateValue) {
            // If already in YYYY-MM-DD format, return as is
            if (!dateValue) return '';
            
            // If it's a string in YYYY-MM-DD format, return it
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                return dateValue;
            }
            
            // If it's a Date object or other format, convert it
            let date;
            if (dateValue instanceof Date) {
                date = dateValue;
            } else {
                date = new Date(dateValue);
            }
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return '';
            }
            
            // Format to YYYY-MM-DD
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            
            return `${year}-${month}-${day}`;
        }
    }
}

</script>

<style scoped>
/* Phone Card - Enhanced Android Style */
.phone-card {
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    border: 1px solid rgba(0, 0, 0, 0.06);
    position: relative;
}

.phone-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.phone-card:hover::before {
    opacity: 1;
}

.phone-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
}

/* Card Body */
.phone-card-body {
    display: flex;
    gap: 12px;
    padding: 20px 18px;
    position: relative;
}

/* Horizontal Battery Wrapper */
.battery-wrapper {
    display: flex;
    align-items: center;
    padding-top: 8px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
}

.battery-horizontal {
    width: 52px;
    height: 28px;
}

.battery-wrapper.status-grey {
    color: #777D85;
}

.battery-wrapper.status-red {
    color: #FF0000;
}

.battery-wrapper.status-green {
    color: #5BC236;
}

.battery-wrapper.status-orange {
    color: #FF5722;
}

.battery-wrapper.status-yellow {
    color: #FEC200;
}

/* Order Info Area */
.order-info-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 11px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: transform 0.2s ease;
}

.info-row:hover {
    transform: translateX(3px);
}

.info-key {
    font-family: 'Somar-Bold';
    font-size: 14px;
    color: #2B5FA8;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.info-val {
    font-family: 'Somar-Medium';
    font-size: 14px;
    color: #45566D;
}

/* Status Value Styling */
.status-val.status-grey {
    color: #777D85 !important;
    font-family: 'Somar-Bold' !important;
}

.status-val.status-red {
    color: #FF0000 !important;
    font-family: 'Somar-Bold' !important;
}

.status-val.status-green {
    color: #5BC236 !important;
    font-family: 'Somar-Bold' !important;
}

.status-val.status-orange {
    color: #FF5722 !important;
    font-family: 'Somar-Bold' !important;
}

.status-val.status-yellow {
    color: #FEC200 !important;
    font-family: 'Somar-Bold' !important;
}

/* Details Footer */
.details-footer {
    background: linear-gradient(180deg, #D1E4FF 0%, #C5DBFA 100%);
    padding: 15px;
    text-align: center;
    font-family: 'Somar-Bold';
    font-size: 17px;
    color: #45566D;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.details-footer::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(60, 137, 206, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.4s ease, height 0.4s ease;
}

.details-footer:hover::after {
    width: 300px;
    height: 300px;
}

.details-footer:hover {
    background: linear-gradient(180deg, #3C89CE 0%, #2274BD 100%);
    color: #fff;
    box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.15);
}

.details-footer span {
    position: relative;
    z-index: 1;
}

/* New Order Button */
.new-order {
    background-image: linear-gradient(90deg, #FFCA1F 0%, #FFD44A 100%);
    border-radius: 20px;
    color: #fff;
    width: 200px;
    font-size: 21px;
    font-family: 'Somar-bold';
    border-width: 0px;
    transition: all 0.3s ease-in-out;
}

.new-order:hover {
    outline: none;
    border-width: 0px;
    background-image: linear-gradient(90deg, #FFD44A 0%, #FFCA1F 100%);
    color: #fff;
}

/* Pagination */
.pagination {
    justify-content: center;
    display: flex;
    align-items: center;
}

/* Loading */
.loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background-color: #f8f9fa;
}

.logo {
    width: 100px;
    height: auto;
}

/* Responsive */
@media (max-width: 768px) {
    .new-order {
        width: 100%;
        font-size: 18px;
        margin: 10px 0;
    }
    
    .battery-wrapper {
        width: 35px;
    }
    
    .battery-horizontal {
        width: 45px;
        height: 24px;
    }
    
    .info-key, .info-val {
        font-size: 13px;
    }
    
    .phone-card-body {
        padding: 15px 12px;
    }
}

@media (max-width: 768px) {
    .total-count-badge {
        margin-bottom: 10px;
        margin-right: 0 !important;
    }
}

@media (max-width: 576px) {
    .filter-text {
        font-size: 20px !important;
    }
    
    .filter-input {
        width: 120px !important;
    }
    
    .total-count-badge {
        padding: 6px 12px;
        font-size: 12px;
    }
    
    .count-label {
        font-size: 12px;
    }
    
    .count-number {
        font-size: 14px;
        padding: 3px 8px;
        min-width: 30px;
    }
    
    .filter-btn {
        padding: 8px 12px;
        font-size: 13px;
    }
    
    .new-order {
        width: 100%;
    }
}


/* Filter Button */
.filter-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 12px;
    padding: 10px 20px;
    font-family: 'Somar-Bold';
    font-size: 15px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    white-space: nowrap;
    flex-shrink: 0;
}

.filter-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
}

/* Total Count Badge */
.total-count-badge {
    background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
    border-radius: 25px;
    padding: 8px 18px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 4px 12px rgba(32, 201, 151, 0.3);
    animation: slideIn 0.4s ease-out;
    white-space: nowrap;
    flex-shrink: 0;
    min-width: fit-content;
}

@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateX(-20px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.count-label {
    font-family: 'Somar-Bold';
    font-size: 14px;
    color: #ffffff;
}

.count-number {
    background: rgba(255, 255, 255, 0.3);
    padding: 4px 12px;
    border-radius: 15px;
    font-family: 'Somar-Bold';
    font-size: 16px;
    color: #ffffff;
    min-width: 35px;
    text-align: center;
    backdrop-filter: blur(10px);
}

/* Modal Styling */
.filter-label {
    font-family: 'Somar-Bold';
    font-size: 15px;
    color: #2B5FA8;
    margin-bottom: 8px;
    display: block;
}

.custom-datepicker,
.custom-select,
.custom-input {
    border-radius: 10px;
    border: 2px solid #e0e6ed;
    padding: 10px 15px;
    font-family: 'Somar-Medium';
    transition: all 0.3s ease;
}

.custom-datepicker:focus,
.custom-select:focus,
.custom-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
    outline: none;
}

.action-btn {
    border-radius: 12px;
    padding: 12px;
    font-family: 'Somar-Bold';
    font-size: 16px;
    transition: all 0.3s ease;
}

.action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Modal Header */
::v-deep .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1.5rem;
}

::v-deep .modal-header .modal-title {
    font-family: 'Somar-Bold';
    font-size: 22px;
}

::v-deep .modal-header .close {
    color: white;
    opacity: 1;
    text-shadow: none;
}

::v-deep .modal-body {
    padding: 2rem;
}

::v-deep .modal-content {
    border-radius: 0.5rem;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

</style>